/**
 * Toast notification context — provides addToast/removeToast globally.
 * Manages toast lifecycle with auto-dismiss timers.
 */

import { createContext, useContext, useCallback, useState, useRef } from 'react';
import type { ReactNode } from 'react';

// ── Types ──────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  variant: ToastVariant;
  message: string;
  duration: number;
}

export interface ToastContextValue {
  toasts: Toast[];
  addToast: (variant: ToastVariant, message: string, duration?: number) => string;
  removeToast: (id: string) => void;
}

// ── Defaults ───────────────────────────────────────────────────

const DEFAULT_DURATIONS: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 6000,
};

const MAX_VISIBLE = 5;

let toastCounter = 0;

// ── Context ────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (variant: ToastVariant, message: string, duration?: number): string => {
      const id = `toast-${++toastCounter}`;
      const dur = duration ?? DEFAULT_DURATIONS[variant];

      const toast: Toast = { id, variant, message, duration: dur };

      setToasts((prev) => {
        const next = [toast, ...prev];
        // Evict oldest if over max
        if (next.length > MAX_VISIBLE) {
          const evicted = next.slice(MAX_VISIBLE);
          for (const t of evicted) {
            const timer = timersRef.current.get(t.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(t.id);
            }
          }
          return next.slice(0, MAX_VISIBLE);
        }
        return next;
      });

      // Auto-dismiss (0 = persistent)
      if (dur > 0) {
        const timer = setTimeout(() => {
          timersRef.current.delete(id);
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, dur);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
